import { Head, usePage } from "@inertiajs/react"

import {
  LongestInShopTable,
  OldestBadOrdersTable,
} from "@/components/dashboard/AgingTables"
import { FacilitySlaBoard } from "@/components/dashboard/FacilitySlaBoard"
import { FleetStatTiles } from "@/components/dashboard/FleetStatTiles"
import { RequalBurnDownChart } from "@/components/dashboard/RequalBurnDownChart"
import type {
  AgingBadOrder,
  AgingInShop,
  RequalSeries,
} from "@/components/dashboard/types"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem, FleetMetrics, SharedProps } from "@/types"

// The fleet-maintenance dashboard — the page that replaces the customer's
// hand-built Power BI sheet.
//
// Reading order is deliberate and matches how the morning call runs: the four
// numbers everyone quotes, then which refinery is in trouble, then the
// compliance program, then the two lists somebody works from.
interface Props {
  client_name: string
  metrics: FleetMetrics
  requal_series: RequalSeries
  aging_bad_orders: AgingBadOrder[]
  aging_in_shop: AgingInShop[]
}

export default function Dashboard({
  client_name,
  metrics,
  requal_series,
  aging_bad_orders,
  aging_in_shop,
}: Props) {
  const routes = useOrgRoutes()
  const { organization } = usePage<SharedProps>().props

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Dashboard", href: routes.dashboardPath() },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`${client_name} — Fleet dashboard`} />

      <div className="space-y-6 p-4 sm:p-6">
        <div>
          <h1 className="text-2xl font-semibold">
            {organization?.name ?? client_name}
          </h1>
          <p className="text-muted-foreground">
            Where every bad ordered car stands today
          </p>
        </div>

        <FleetStatTiles metrics={metrics} />

        <FacilitySlaBoard rows={metrics.facility_sla} />

        <RequalBurnDownChart
          burnDown={metrics.requal_burn_down}
          series={requal_series}
        />

        <div className="grid gap-6 xl:grid-cols-2">
          <OldestBadOrdersTable rows={aging_bad_orders} />
          <LongestInShopTable rows={aging_in_shop} />
        </div>
      </div>
    </AppLayout>
  )
}
